import Client from '@walletconnect/sign-client';
import type { PairingTypes, SessionTypes } from '@walletconnect/types';
import { getSdkError } from '@walletconnect/utils';
import { WalletConnectModal } from '@walletconnect/modal';

export enum DEFAULT_CHIA_METHODS {
  CHIA_GET_WALLETS = 'chia_getWallets',
  CHIA_SEND_TRANSACTION = 'chia_sendTransaction',
  CHIA_NEW_ADDRESS = 'chia_getNextAddress',
  CHIA_LOG_IN = 'chia_logIn',
  CHIA_SIGN_MESSAGE_BY_ADDRESS = 'chia_signMessageByAddress',
  CHIA_SIGN_MESSAGE_BY_ID = 'chia_signMessageById',
  CHIA_CREATE_OFFER = 'chia_createOfferForIds',
  CHIA_TAKE_OFFER = 'chia_takeOffer',
  CHIA_GET_NFT_INFO = 'chia_getNFTInfo',
  CHIA_GET_WALLET_SYNC_STATUS = 'chia_getSyncStatus',
}

const DEFAULT_RELAY_URL = 'wss://relay.walletconnect.com';
const PROJECT_ID = 'c4a526d68137397a45fef408ba2e4e5e';

export const useWalletConnect = async () => {
  const config = useRuntimeConfig();
  const chainId = `chia:${config.public.network}`;

  const client = ref<Client>();
  const pairings = ref<PairingTypes.Struct[]>([]);
  const session = ref<SessionTypes.Struct>();

  const isInitializing = ref(false);
  const prevRelayerValue = ref<string>('');

  const balances = ref<any>({});
  const accounts = ref<string[]>([]);
  const chains = ref<string[]>([chainId]);
  const relayerRegion = ref<string>(DEFAULT_RELAY_URL);
  const walletConnectModal = new WalletConnectModal({
    projectId: PROJECT_ID,
    explorerRecommendedWalletIds: 'NONE',
    themeVariables: {
      '--wcm-font-family': 'Montserrat, sans-serif',
      '--wcm-background-color': '#059669',
      '--wcm-accent-color': '#059669',
    },
  });

  const reset = () => {
    session.value = undefined;
    chains.value = [];
    accounts.value = [];
    balances.value = {};
    relayerRegion.value = DEFAULT_RELAY_URL;
  };

  const onSessionConnected = (_session: SessionTypes.Struct) => {
    const allNamespaceAccounts = Object.values(_session.namespaces)
      .map((namespace) => namespace.accounts)
      .flat();

    session.value = _session;
    // chains.value = allNamespaceChains;
    accounts.value = allNamespaceAccounts;
    // await getAccountBalances(allNamespaceAccounts);
  };

  const connect = async (pairing: any) => {
    if (typeof client.value === 'undefined') {
      throw new TypeError('WalletConnect is not initialized');
    }
    console.log('connect, pairing topic is:', pairing?.topic);
    try {
      const requiredNamespaces = {
        chia: {
          methods: Object.values(DEFAULT_CHIA_METHODS),
          chains: toRaw(chains.value),
          events: [],
        },
      };
      console.log('requiredNamespaces config for connect:', requiredNamespaces);

      const { uri, approval } = await client.value.connect({
        pairingTopic: pairing?.topic,
        requiredNamespaces,
      });

      // Open QRCode modal if a URI was returned (i.e. we're not connecting an existing pairing).
      if (uri) {
        await walletConnectModal.openModal({ uri });
      }

      const session = await approval();
      console.log('Established session:', session);
      await onSessionConnected(session);
      // Update known pairings after session is connected.
      pairings.value = client.value.pairing.getAll({ active: true });
    } catch (e) {
      console.error(e);
      // ignore rejection
    } finally {
      // close modal in case it was open
      walletConnectModal.closeModal();
    }
  };

  const disconnect = async () => {
    if (typeof client.value === 'undefined') {
      throw new TypeError('WalletConnect is not initialized');
    }
    if (typeof session.value === 'undefined') {
      throw new TypeError('Session is not connected');
    }
    await client.value.disconnect({
      topic: session.value.topic,
      reason: getSdkError('USER_DISCONNECTED'),
    });
    // Reset app state after disconnect.
    reset();
  };

  const subscribeToEvents = (_client: Client) => {
    if (typeof _client === 'undefined') {
      throw new TypeError('WalletConnect is not initialized');
    }

    _client.on('session_ping', (args) => {
      console.log('EVENT', 'session_ping', args);
    });

    _client.on('session_event', (args) => {
      console.log('EVENT', 'session_event', args);
    });

    _client.on('session_update', ({ topic, params }) => {
      console.log('EVENT', 'session_update', { topic, params });
      const { namespaces } = params;
      const _session = _client.session.get(topic);
      const updatedSession = { ..._session, namespaces };
      onSessionConnected(updatedSession);
    });

    _client.on('session_delete', () => {
      console.log('EVENT', 'session_delete');
      reset();
    });
  };

  const _checkPersistedState = async (_client: Client) => {
    if (typeof _client === 'undefined') {
      throw new TypeError('WalletConnect is not initialized');
    }
    // populates existing pairings to state
    pairings.value = _client.pairing.getAll({ active: true });
    console.log('RESTORED PAIRINGS: ', _client.pairing.getAll({ active: true }));

    if (typeof session.value !== 'undefined') return;
    // populates (the last) existing session to state
    if (_client.session.length) {
      const lastKeyIndex = _client.session.keys.length - 1;
      const _session = _client.session.get(_client.session.keys[lastKeyIndex]);
      console.log('RESTORED SESSION:', _session);
      await onSessionConnected(_session);
      return _session;
    }
  };

  if (process.client) {
    try {
      isInitializing.value = true;

      const _client = await Client.init({
        logger: 'debug',
        relayUrl: relayerRegion.value,
        projectId: PROJECT_ID,
        metadata: {
          name: 'MintGarden',
          description: 'Explore and mint NFTs in the Chia ecosystem.',
          url: 'https://mintgarden.io',
          icons: ['https://assets.mainnet.mintgarden.io/web/mint-logo-round.svg'],
        },
      });

      console.log('CREATED CLIENT: ', _client);
      console.log('relayerRegion ', relayerRegion.value);

      client.value = _client;
      prevRelayerValue.value = relayerRegion.value;
      subscribeToEvents(_client);
      await _checkPersistedState(_client);
    } finally {
      isInitializing.value = false;
    }
  }

  const signMessageById = async (id: string, message: string) => {
    const method = DEFAULT_CHIA_METHODS.CHIA_SIGN_MESSAGE_BY_ID;
    try {
      const fingerprint = accounts.value[0].split(':')[2];
      const result = await client.value!.request({
        topic: session.value!.topic,
        chainId,
        request: {
          method,
          params: {
            fingerprint,
            id,
            message,
          },
        },
      });

      return {
        method,
        valid: true,
        result,
      };
    } catch (e) {
      console.error(e);
      return { method, valid: false };
    }
  };

  const createOffer = async (offer: any, driverDict: any) => {
    const method = DEFAULT_CHIA_METHODS.CHIA_CREATE_OFFER;
    try {
      const fingerprint = accounts.value[0].split(':')[2];
      const result = await client.value!.request({
        topic: session.value!.topic,
        chainId,
        request: {
          method,
          params: {
            fingerprint,
            offer,
            driverDict,
            disableJSONFormatting: true,
          },
        },
      });

      return {
        method,
        valid: true,
        result,
      };
    } catch (e) {
      console.error(e);
      return { method, valid: false };
    }
  };

  const getNftInfo = async (coinId: string) => {
    const method = DEFAULT_CHIA_METHODS.CHIA_GET_NFT_INFO;
    try {
      const fingerprint = accounts.value[0].split(':')[2];
      const result = await client.value!.request({
        topic: session.value!.topic,
        chainId,
        request: {
          method,
          params: {
            fingerprint,
            coinId,
          },
        },
      });

      return {
        method,
        valid: true,
        result,
      };
    } catch (e) {
      console.error(e);
      return { method, valid: false };
    }
  };

  const takeOffer = async (offer: string, fee = 0) => {
    const method = DEFAULT_CHIA_METHODS.CHIA_TAKE_OFFER;
    try {
      const fingerprint = accounts.value[0].split(':')[2];
      const result = await client.value!.request({
        topic: session.value!.topic,
        chainId,
        request: {
          method,
          params: {
            fingerprint,
            offer,
            fee: fee * 10 ** 12,
          },
        },
      });

      return {
        method,
        valid: true,
        result,
      };
    } catch (e) {
      console.error(e);
      return { method, valid: false };
    }
  };

  const getWallets = async () => {
    const method = DEFAULT_CHIA_METHODS.CHIA_GET_WALLETS;
    try {
      const fingerprint = accounts.value[0].split(':')[2];
      const result = await client.value!.request({
        topic: session.value!.topic,
        chainId,
        request: {
          method,
          params: {
            fingerprint,
            includeData: true,
          },
        },
      });

      return {
        method,
        valid: true,
        result,
      };
    } catch (e) {
      console.error(e);
      return { method, valid: false };
    }
  };

  return {
    client,
    session,
    pairings,
    connect,
    disconnect,
    signMessageById,
    getWallets,
    getNftInfo,
    createOffer,
    takeOffer,
    accounts: computed(() => accounts.value.map((account) => account.split(':')[2])),
  };
};
